import React, { useState, useEffect } from 'react';
import { Save, ArrowLeft, Check, X, AlertCircle } from 'lucide-react';
import { useParams } from 'react-router';
import axios from 'axios';

const UpdateMQTTSetting = ({ initialData }) => {
  const {id, deviceid} = useParams();
  
  const fetchMqttSetting = async (id) => {
    const token = getCookie("token");
    const userid = getCookie("userid");
    try {
      const response = await axios.get(`https://api.dqr-display-tms.bonrix.in/api/MQTTSettingOperation/${id}`, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          accept: "text/plain",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching MQTT setting:", error);
      throw error;
    }
  };

  const [formData, setFormData] = useState({
    id: id,
    active: true,
    lockkey: true,
    remark: "",
    deviceid: "",
    json: "{}"
  });

  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Enhanced JSON validation
  const isJsonValid = (jsonString) => {
    if (!jsonString || jsonString.trim() === "") {
      return true;
    }
    try {
      JSON.parse(jsonString);
      return true;
    } catch (error) {
      return false;
    }
  };

  // Improved JSON stringification with better error handling
  const ensureJsonStringified = (jsonValue) => {
    if (!jsonValue || jsonValue.trim() === "") {
      return "{}";
    }
    
    try {
      // First, try to parse it to validate it's valid JSON
      const parsedJson = JSON.parse(jsonValue);
      // Then stringify it to ensure consistent formatting
      return JSON.stringify(parsedJson);
    } catch (error) {
      console.error("JSON parsing error:", error);
      return "{}"; // Return empty object if invalid
    }
  };

  // Handle regular input changes
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? e.target.checked : value,
    }));
  };

  // Handle JSON textarea changes
  const handleJsonChange = (e) => {
    const value = e.target.value;
    setFormData((prevData) => ({
      ...prevData,
      json: value,
    }));
  };

  // Format JSON for display (pretty print)
  const formatJson = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch (error) {
      return jsonString;
    }
  };

  // Enhanced form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const token = getCookie("token");
    const userid = getCookie("userid");

    if (!token || !userid) {
      setMessage("Authentication error. Please log in again.");
      return;
    }

    // Validate JSON format
    if (!isJsonValid(formData.json)) {
      setMessage("Invalid JSON format. Please check your JSON configuration.");
      return;
    }

    // Process the JSON - format as escaped JSON string
    let processedJson;
    try {
      if (!formData.json || formData.json.trim() === "") {
        processedJson = "{}";
      } else {
        // Parse to validate, then stringify to get the escaped format
        const parsed = JSON.parse(formData.json);
        processedJson = JSON.stringify(parsed);
      }
    } catch (error) {
      setMessage("Failed to process JSON configuration. Please check the format.");
      return;
    }

    // Create update data with properly processed JSON
    const updateData = {
      id: parseInt(formData.id),
      active: formData.active === "true" || formData.active === true,
      lockkey: formData.lockkey === "true" || formData.lockkey === true,
      remark: formData.remark || "",
      deviceid: formData.deviceid || "",
      json: processedJson
    };

    console.log("Processed JSON:", processedJson);
    console.log("Complete update data:", updateData);

    setIsSubmitting(true);
    setMessage("");

    try {
      const updateUrl = "https://api.dqr-display-tms.bonrix.in/api/DeviceJsonSettingOperation/Update";

      const response = await fetch(updateUrl, {
        method: 'POST',
        headers: {
          "X-Authorization": token,
          "UserId": userid,
          "Content-Type": "application/json-patch+json",
          "accept": "text/plain"
        },
        body: JSON.stringify(updateData)
      });

      const responseData = await response.json().catch(() => null);
      console.log("API Response:", responseData);

      if (response.ok || (responseData && responseData.isresponse && responseData.responsestatus === "SUCCESS")) {
        setMessage("MQTT Setting updated successfully!");
        setTimeout(() => {
          console.log("Would navigate to /mqttSetting");
        }, 3000);
      } else {
        setMessage(`Failed to update MQTT Setting: ${responseData?.errormsg || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error updating MQTT Setting:", error);
      setMessage(`Failed to update MQTT Setting: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto-format JSON when textarea loses focus
  const handleJsonBlur = () => {
    if (isJsonValid(formData.json) && formData.json.trim() !== "") {
      setFormData(prev => ({
        ...prev,
        json: formatJson(formData.json)
      }));
    }
  };

  // Load sample data
    useEffect(() => {
      const loadData = async () => {
        setLoading(true);
        setMessage("");
  
        try {
          let dataToLoad = null;
  
          // Priority 1: Use initialData prop if provided
          if (initialData) {
            dataToLoad = initialData;
          }
          // Priority 2: Fetch from API if settingId is provided
          else if (id) {
            dataToLoad = await fetchMqttSetting(id);
          }
          // Priority 3: Use empty form for new entries
          else {
            dataToLoad = {
              id: null,
              active: true,
              lockkey: true,
              remark: "",
              deviceid: "",
              json: "{}"
            };
          }
  
          if (dataToLoad) {
            setFormData({
              id: dataToLoad.id || id,
              active: dataToLoad.active !== undefined ? dataToLoad.active : true,
              lockkey: dataToLoad.lockkey !== undefined ? dataToLoad.lockkey : true,
              remark: dataToLoad.remark || "",
              deviceid: dataToLoad.deviceid || "",
              json: dataToLoad.json ? formatJson(dataToLoad.json) : "{}"
            });
          }
        } catch (error) {
          setMessage(`Error loading data: ${error.message}`);
          // Set default values on error
          setFormData({
            id: id,
            active: true,
            lockkey: true,
            remark: "",
            deviceid: "",
            json: "{}"
          });
        } finally {
          setLoading(false);
        }
      };
  
      loadData();
    }, [id, initialData]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button className="mr-4 p-2 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                Update MQTT Setting
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              Device JSON Setting Configuration
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Update the MQTT device settings and JSON configuration
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="p-6">
              <div className="space-y-6">
                {/* Device ID and Remark */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Device ID *
                    </label>
                    <input
                      type="text"
                      name="deviceid"
                      value={formData.deviceid}
                      onChange={handleChange}
                      placeholder="Enter device ID..."
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description/Remark
                    </label>
                    <input
                      type="text"
                      name="remark"
                      value={formData.remark}
                      onChange={handleChange}
                      placeholder="Enter description..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                {/* Active and Lock Key */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Active Status
                    </label>
                    <select
                      name="active"
                      value={formData.active}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value={true}>Active</option>
                      <option value={false}>Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Lock Key
                    </label>
                    <select
                      name="lockkey"
                      value={formData.lockkey}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value={true}>Enabled</option>
                      <option value={false}>Disabled</option>
                    </select>
                  </div>
                </div>

                {/* JSON Configuration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    JSON Configuration *
                  </label>
                  <div className="mb-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Enter valid JSON configuration. Auto-formats when you click outside the textarea.
                    </p>
                  </div>
                  <textarea
                    name="json"
                    value={formData.json}
                    onChange={handleJsonChange}
                    onBlur={handleJsonBlur}
                    placeholder='Enter JSON configuration... e.g., {"key": "value"}'
                    rows={10}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 font-mono text-sm ${
                      !isJsonValid(formData.json)
                        ? 'border-red-300 focus:ring-red-500 bg-red-50 dark:bg-red-950'
                        : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:bg-gray-700 dark:text-white'
                    }`}
                  />
                  {!isJsonValid(formData.json) && (
                    <div className="mt-1 flex items-center text-red-600 text-sm">
                      <X className="w-4 h-4 mr-1" />
                      Please enter valid JSON format
                    </div>
                  )}
                  {isJsonValid(formData.json) && formData.json && (
                    <div className="mt-1 flex items-center text-green-600 text-sm">
                      <Check className="w-4 h-4 mr-1" />
                      Valid JSON format
                    </div>
                  )}
                </div>

                {/* Final JSON Preview */}
                {isJsonValid(formData.json) && (
                  <div className="bg-blue-50 dark:bg-blue-950 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                      Final JSON String (sent to API):
                    </h3>
                    <div className="bg-white dark:bg-gray-800 rounded border p-3 text-xs font-mono overflow-x-auto">
                      <code className="text-blue-800 dark:text-blue-200">
                        "{formData.json ? JSON.stringify(JSON.parse(formData.json || "{}")) : "{}"}"
                      </code>
                    </div>
                    <div className="mt-2 text-xs text-blue-700 dark:text-blue-300">
                      Example formats:
                      <div className="mt-1 space-y-1">
                        <div>• Empty: <code>"json": "{}"</code></div>
                        {/* <div>• With ID: <code>"json": "{\"id\":6}"</code></div>
                        <div>• Complex: <code>"json": "{\"broker\":\"mqtt.example.com\",\"port\":1883}"</code></div> */}
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center gap-4 pt-4">
                  <button
                    onClick={handleSubmit}
                    disabled={!isJsonValid(formData.json) || isSubmitting}
                    className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Updating...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Update MQTT Setting
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => console.log("Would navigate back")}
                    className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Status Messages */}
        {message && (
          <div className="mt-6">
            {message === "MQTT Setting updated successfully!" ? (
              <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center">
                  <Check className="w-5 h-5 text-green-500 mr-2" />
                  <p className="text-green-800 dark:text-green-200 font-medium">{message}</p>
                </div>
              </div>
            ) : (
              <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                  <p className="text-red-800 dark:text-red-200 font-medium">{message}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateMQTTSetting;
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}
